import { TicketData } from '../types/printerProfile';
import {
  TICKET_WIDTH,
  doubleSeparator,
  separatorLine,
  centerText,
  formatLine,
  formatTotalLine,
} from './ticketFormatter';
import { formatOrderNumber } from './orderNumberFormatter';

export const buildMinimalTicket = (data: TicketData): string => {
  const lines: string[] = [];
  const currency = 'EUR';
  const dateStr = new Date().toLocaleString('fr-FR');
  const orderTypeStr = data.orderType === 'takeaway' ? data.t('À emporter') : data.t('Sur place');
  const tableStr = data.tableNumber ? ` - ${data.t('Table')} ${data.tableNumber}` : '';
  const terminalName = data.profileSettings?.terminalName || 'Borne 1';

  lines.push('[HEADER:START]');
  lines.push('\x1B\x21\x18');
  if (data.storeInfo?.name) {
    lines.push(centerText(data.storeInfo.name));
  }
  lines.push(doubleSeparator());
  lines.push(centerText(`**** #${formatOrderNumber(data.orderCode)} ****`));
  lines.push(centerText(terminalName));
  lines.push(doubleSeparator());
  lines.push('\x1B\x21\x00');
  lines.push(formatLine(`${data.t('Date')}:`, dateStr));
  lines.push(formatLine(`${data.t('Type')}:`, `${orderTypeStr}${tableStr}`));
  if (data.customerName) {
    lines.push(formatLine(`${data.t('Client')}:`, data.customerName));
  }
  lines.push('[HEADER:END]');

  lines.push(separatorLine('-'));

  lines.push(centerText(data.t('TICKET CLIENT')));
  lines.push(centerText(data.t('(Sans détail des produits)')));

  lines.push(separatorLine('-'));

  lines.push('[TOTALS:START]');

  // v5 fidélité: si réduction, on l'ajoute en ligne séparée et on adapte le total
  const loyaltyDiscount = data.loyaltyDiscount || 0;
  const loyaltyPointsUsed = data.loyaltyPointsUsed || 0;
  // v5 happy hour (NF525: justifie un prix != catalogue)
  const happyHourDiscount = data.happyHourDiscount || 0;
  const happyHourPercent = data.happyHourPercent || 0;

  if (data.taxSettings?.displayMode !== 'hidden') {
    if (data.taxSettings?.displayMode === 'detailed') {
      const subtotalHT = (data.taxResult?.subtotalHT ?? data.orderTotals.subtotal).toFixed(2);
      lines.push(formatLine(`${data.t('Sous-total HT')}:`, `${subtotalHT}${currency}`));

      const taxAmt = (data.taxResult?.taxAmount ?? data.orderTotals.taxAmount).toFixed(2);
      if (data.taxResult?.taxRate !== null) {
        const taxRateFormatted = (data.taxResult?.taxRate ?? 0) % 1 === 0
          ? (data.taxResult?.taxRate ?? 0).toFixed(0)
          : (data.taxResult?.taxRate ?? 0).toFixed(1);
        lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
      } else {
        lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
      }

      if (happyHourDiscount > 0) {
        lines.push(formatLine(
          `${data.t('Happy Hour')} -${happyHourPercent}%:`,
          `-${happyHourDiscount.toFixed(2)}${currency}`
        ));
      }

      if (loyaltyDiscount > 0) {
        lines.push(formatLine(
          `${data.t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
          `-${loyaltyDiscount.toFixed(2)}${currency}`
        ));
      }

      lines.push(doubleSeparator());
      const totalBeforeDiscount = data.taxResult?.totalTTC ?? data.orderTotals.invoiceTotal;
      const totalFinal = Math.max(0, totalBeforeDiscount - loyaltyDiscount - happyHourDiscount);
      lines.push(formatTotalLine(`Total TTC:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
    } else if (data.taxSettings?.displayMode === 'simple') {
      const taxAmt = (data.taxResult?.taxAmount ?? data.orderTotals.taxAmount).toFixed(2);
      if (data.taxResult?.taxRate !== null) {
        const taxRateFormatted = (data.taxResult?.taxRate ?? 0) % 1 === 0
          ? (data.taxResult?.taxRate ?? 0).toFixed(0)
          : (data.taxResult?.taxRate ?? 0).toFixed(1);
        lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
      } else {
        lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
      }

      if (happyHourDiscount > 0) {
        lines.push(formatLine(
          `${data.t('Happy Hour')} -${happyHourPercent}%:`,
          `-${happyHourDiscount.toFixed(2)}${currency}`
        ));
      }

      if (loyaltyDiscount > 0) {
        lines.push(formatLine(
          `${data.t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
          `-${loyaltyDiscount.toFixed(2)}${currency}`
        ));
      }

      lines.push(doubleSeparator());
      const totalBeforeDiscount = data.taxResult?.totalTTC ?? data.orderTotals.invoiceTotal;
      const totalFinal = Math.max(0, totalBeforeDiscount - loyaltyDiscount - happyHourDiscount);
      lines.push(formatTotalLine(`${data.t('Total')}:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
    }
  } else {
    if (happyHourDiscount > 0) {
      lines.push(formatLine(
        `${data.t('Happy Hour')} -${happyHourPercent}%:`,
        `-${happyHourDiscount.toFixed(2)}${currency}`
      ));
    }
    if (loyaltyDiscount > 0) {
      lines.push(formatLine(
        `${data.t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
        `-${loyaltyDiscount.toFixed(2)}${currency}`
      ));
    }
    lines.push(doubleSeparator());
    const totalBeforeDiscount = data.taxResult?.totalTTC ?? data.orderTotals.invoiceTotal;
    const totalFinal = Math.max(0, totalBeforeDiscount - loyaltyDiscount - happyHourDiscount);
    lines.push(formatTotalLine(`${data.t('Total')}:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
  }

  lines.push('\x1B\x21\x00');
  lines.push(doubleSeparator());
  const paymentStr = data.paymentMethod
    ? data.t(data.paymentMethod.charAt(0).toUpperCase() + data.paymentMethod.slice(1))
    : data.t('Non spécifié');
  lines.push(formatLine(`${data.t('Paiement')}:`, paymentStr));
  lines.push('[TOTALS:END]');

  if (data.profileSettings?.siretMessage && data.profileSettings.siretMessage.trim() !== '') {
    lines.push(separatorLine('-'));
    lines.push(centerText(data.profileSettings.siretMessage));
  }
  if (data.profileSettings?.tvaMessage && data.profileSettings.tvaMessage.trim() !== '') {
    lines.push(separatorLine('-'));
    lines.push(centerText(data.profileSettings.tvaMessage));
  }

  lines.push(separatorLine('-'));
  lines.push('[FOOTER:START]');
  lines.push(centerText(data.t('Merci de votre visite')));
  lines.push(doubleSeparator());
  lines.push('[FOOTER:END]');

  return lines.join('\n');
};
